import React, { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import { isDevAffordanceEnabled } from '../utils/devMode';
import { pathwaysForConcern, pathwayTriageDocuments, PATHWAY_LABELS } from '../utils/triagePathways';
/**
 * ServiceUserPatientOverview
 * --------------------------
 * Purpose:
 *   - Per‑referral (patient/CYP) overview for a signed‑in RMS user.
 *   - Shows the “People involved in this referral” panel and the list of
 *     post‑account forms relevant to this referral.
 *
 * Implementation:
 *   - Expects a referralId in the URL (query param) and uses it to construct
 *     routes to individual forms (e.g., Request for Referral, Education History).
 *   - Group progress is derived client‑side for now; later this will be driven
 *     by server data and real completion status.
 */
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import useOrgHfpInstrument from '../hooks/useOrgHfpInstrument';
import { auth, db, functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
import { doc, updateDoc, onSnapshot } from 'firebase/firestore';
import PeopleDialog from '../components/PeopleDialog';
import AssignTriageFormsDialog from '../components/AssignTriageFormsDialog';
import AutoCompleteButton from '../components/AutoCompleteButton';
import RmsAllFormsCompletedModal from '../components/RmsAllFormsCompletedModal';
import RmsTriageCompleteModal from '../components/RmsTriageCompleteModal';
import InfoTooltip from '../components/InfoTooltip';
import { UserGroupIcon, HomeIcon, ClipboardListIcon, DocumentTextIcon } from '@heroicons/react/outline';
import { CheckCircleIcon, LockClosedIcon } from '@heroicons/react/solid';
import {
  shouldShowAssignTriageFormsPanel,
  requiredReferralCompletionKeys,
  serviceUserFormGroupVisibility,
  isAdhdConcern as deriveIsAdhdConcern,
  otherPartyRequiredFormKeys,
  isHfpReportAvailable,
} from './serviceUserPatientOverviewGating';
import {
  completionPercent,
  areRoleInstancesComplete,
  getServiceUserReferralStatus,
  isSenOnlyRecord,
  buildFormProgressFromRecord,
  HFP_FORM_KEYS as HFP_V2_FORM_KEYS,
  HFP_V3_FORM_KEYS,
  hfpFormKeysForRecord,
  hfpInstrumentForRecord,
} from '../utils/referralStatus';
import { HARBOUR_FORM_SLUGS } from '../utils/harbourForms';
import {
  recordRequiresSubmissionConfirmation,
  isRoleSubmissionConfirmed,
  canConfirmSubmission,
} from '../utils/submissionConfirmation';
import { apiConfirmSubmission } from '../utils/rmsApi';
import ConfirmAndSubmitScreen from '../components/ConfirmAndSubmitScreen';

// Environment check for showing dev tools (auto-complete buttons).
// Only visible in local development only.
const isDev = isDevAffordanceEnabled();

// Form keys that must be completed for "all forms complete" detection
// Used by the completion modal to determine when to show celebration
const REFERRAL_FORM_KEYS = [
  'request-for-referral',
  'reason-for-referral', 
  'education-history',
  'snap-iv-26',
];

// The seven HFP domains every instrument generation shares (v2 and v3). The
// record's instrument (hfpInstrumentForRecord, TDI-414) decides which
// generation's forms are listed; the domain decides the title and subtitle.
const HFP_DOMAIN_ITEMS = {
  ExecutiveFunction: { title: 'Executive Function', subtitle: 'Focus, planning, organising and adapting' },
  MotorPhysical: { title: 'Motor & Physical', subtitle: 'Movement, coordination and physical skills' },
  SpeechLanguageCommunication: { title: 'Speech, Language & Communication', subtitle: 'Understanding and expressing themselves' },
  SocialEmotional: { title: 'Social & Emotional', subtitle: 'Feelings, relationships and managing emotions' },
  Sensory: { title: 'Sensory', subtitle: 'How they experience and respond to sensory input' },
  GlobalParticipation: { title: 'Global Participation', subtitle: 'Taking part in everyday activities and life' },
  EnvironmentalFactors: { title: 'Environmental Factors', subtitle: 'Supports and barriers around them' },
};

/** The overview list items (route slug + backend formKey) for a set of HFP form keys. */
function hfpListItems(formKeys) {
  return formKeys
    .map((formKey) => {
      const domain = HFP_DOMAIN_ITEMS[formKey.replace(/^hfpV\d+/, '')];
      const key = HARBOUR_FORM_SLUGS[formKey];
      return domain && key ? { ...domain, key, formKey } : null;
    })
    .filter(Boolean);
}

// The guardian's referral history forms: part of the referral (Tom King, 28 October 2026).
// These use URL-friendly kebab-case keys for consistency with route params
const TRIAGE_FORM_KEYS = [
  'their-early-life',
  'their-family-and-home-life',
  'their-lifestyle',
  'their-history-with-the-law',
  'their-family-history',
  'their-medical-history',
  'their-mental-health-history',
];

/**
 * formatCreatedAt - Formats a Firestore timestamp or date for display.
 * Handles various timestamp formats (ISO string, Firestore Timestamp object).
 * Returns a human-readable string like "3:45pm on 15th Jan 2024".
 */
function formatCreatedAt(value) {
  if (!value) return '-';
  try {
    let date = null;
    if (typeof value === 'string') {
      const direct = new Date(value);
      if (!isNaN(direct.getTime())) {
        date = direct;
      } else {
        const m = value.match(/seconds=(\d+)/);
        if (m && m[1]) {
          date = new Date(parseInt(m[1], 10) * 1000);
        }
      }
    } else if (value && typeof value === 'object') {
      if (typeof value.seconds === 'number') {
        date = new Date(value.seconds * 1000);
      }
      if (!date && typeof value.toDate === 'function') {
        date = value.toDate();
      }
    }
    if (!date) return '-';
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const hour12 = hours % 12 || 12;
    const ampm = hours < 12 ? 'am' : 'pm';
    const minuteStr = minutes < 10 ? `0${minutes}` : `${minutes}`;
    const day = date.getDate();
    const daySuffix = (d) => {
      if (d >= 11 && d <= 13) return 'th';
      switch (d % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };
    const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${hour12}:${minuteStr}${ampm} on ${day}${daySuffix(day)} ${month} ${year}`;
  } catch (_) {
    return '-';
  }
}

export default function ServiceUserPatientOverview() {
  const navigate = useNavigate();
  const { patientRecordId = 'demo' } = useParams();
  const { claims } = useAuth();
  const orgId = claims?.rmsOrgId;
  const [isPeopleDialogOpen, setIsPeopleDialogOpen] = useState(false);
  const [isTriageFormsDialogOpen, setIsTriageFormsDialogOpen] = useState(false);
  // HFP (Holistic Functional Profiling) Report Dialog
  
  // All Forms Completed Modal - shown when user completes all referral + HFP forms
  // Shows confetti celebration and dynamic next steps (info-only, no feedback collection for now)
  const [isAllFormsCompletedModalOpen, setIsAllFormsCompletedModalOpen] = useState(false);
  // Harbour pupils (SEN & ND Access Portal) are never shown this legacy page:
  // parents go to their child's page, school users to the pupil record.
  const [isHarbourPupil, setIsHarbourPupil] = useState(false);
  // The record's HFP instrument (TDI-523): its pin once a form has opened,
  // else the organisation's live setting, else the pathway default. The two
  // fields the resolver reads are kept from the record snapshot; the
  // instrument itself is derived so the 16 Nov switch reaches an open page.
  const [hfpRecord, setHfpRecord] = useState(null);
  const orgHfpInstrument = useOrgHfpInstrument(orgId);
  const hfpInstrument = useMemo(
    () => hfpInstrumentForRecord(hfpRecord, orgHfpInstrument),
    [hfpRecord, orgHfpInstrument],
  );
  const hfpFormKeys = useMemo(() => hfpFormKeysForRecord({ hfpInstrument }), [hfpInstrument]);
  // Raw modal-dismissal maps from the patient record (cross-device persistence),
  // streamed by the record snapshot below; null until the first snapshot lands.
  // The per-role booleans are derived where the viewer's role is known.
  const [modalDismissedRaw, setModalDismissedRaw] = useState(null); // { completion: {...}, triage: {...} } | null

  // Triage Forms Completed Modal - shown when guardian completes all 7 triage forms
  // Provides closure and explains next steps (referral ready for clinical review)
  const [isTriageCompleteModalOpen, setIsTriageCompleteModalOpen] = useState(false);
  const hasShownTriageModalRef = useRef(false); // Prevents showing modal multiple times per session

  // Flow context (initiator/role) no longer rendered; keep patient info only
  const [patientInfo, setPatientInfo] = useState(null); // { name, dob, createdAt, orgName }
  // Track whether triage forms have already been assigned for this patient
  const [triageFormsAssigned, setTriageFormsAssigned] = useState(false);
  // Confirm-and-submit (TDI-415): gate inputs from the record snapshot, plus
  // the call state for the confirm action itself.
  const [confirmGate, setConfirmGate] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmFailed, setConfirmFailed] = useState(false);
  // The record's formProgress mirror (TDI-154): the backend copies each form
  // instance's headline { status, percent } onto the patient record, which
  // participants can read — client reads of the formInstances subcollection
  // are provider-only. Covers BOTH roles' instances; the viewer's formProgress
  // and the other party's completion are BOTH derived from this single source
  // (TD-156), so the two can never disagree. null until the first record
  // snapshot lands; missing on old records pre-backfill folds to {} (every
  // form reads as not started, same as before any instance existed).
  const [recordFormProgress, setRecordFormProgress] = useState(null); // null = loading
  const [viewerRoleSuffix, setViewerRoleSuffix] = useState(null); // 'school_user' | 'guardian' | null
  // Whether the HFP report has been generated for this referral. The HFP is only
  // written to `patientRecord.domains.hfp` once both respondents have submitted, so
  // its presence is used to decide whether to surface the Documents section at all.
  const [hfpAvailable, setHfpAvailable] = useState(false);
  // Triage outcome letters (TD-145): one entry per pathway a letter exists for
  // (a combined referral can have both an ADHD and an autism letter). Each entry
  // records which service-user roles a clinician has sent it to and whether its
  // PDF exists — a recipient sees a letter only once it has been sent to their
  // role. { pathway, label, sharedRoles, hasPdf }.
  const [triageLetters, setTriageLetters] = useState([]);
  // The pathway whose letter is currently being opened (null when idle).
  const [viewingLetterPathway, setViewingLetterPathway] = useState(null);
  // Triage state for the plain-language referral status shown to the service user
  // (TD-14): the triage lock status and recorded reviews from the patient record.
  const [referralTriage, setReferralTriage] = useState({ status: null, reviews: [] });
  // School phase ('primary' | 'secondary' | null) from the referral. Secondary
  // referrals skip the HFP and the proceed-with-triage panel (TD-129 scope).
  const [schoolPhase, setSchoolPhase] = useState(null);
  const isSecondaryReferral = schoolPhase === 'secondary';
  // Intake concern ('asd' | 'adhd' | 'both' | null) from the referral, derived
  // server-side from either referrer's reason for referral. The ADHD SNAP-IV
  // symptom scale stays hidden until an ADHD concern (adhd/both) has been logged
  // by the school or the parent.
  const [intakeConcern, setIntakeConcern] = useState(null);
  // Harbour SEN-only pupil (TDI-53): on the SEN pathway with NO referral on
  // the record. The overview then shows only the HFP forms — a parent must
  // never be asked referral questions for a referral that does not exist.
  const [senOnlyRecord, setSenOnlyRecord] = useState(false);
  const isAdhdConcern = deriveIsAdhdConcern(intakeConcern);

  // Open the HFP v2 profile on its dedicated page (mirrors the clinician view).
  const handleViewHfpV2 = useCallback(() => {
    navigate(`/rms/patient/${patientRecordId}/hfp`);
  }, [navigate, patientRecordId]);

  // Open the sent triage outcome letter via a short-lived signed URL (TD-145).
  // The backend allows the fetch only once the letter has been sent to this
  // recipient's role.
  const handleViewTriageLetter = useCallback(async (pathway) => {
    if (viewingLetterPathway || !orgId || !patientRecordId) return;
    setViewingLetterPathway(pathway || 'default');
    try {
      const fn = httpsCallable(functions, 'rmsFetchTriageDocument', { timeout: 30000 });
      const res = await fn({
        orgId,
        patientRecordId,
        fileFormat: 'pdf',
        disposition: 'inline',
        ...(pathway ? { pathway } : {}),
      });
      const url = res?.data?.downloadUrl;
      if (url) window.open(url, '_blank');
    } catch (err) {
      console.error('Failed to open triage outcome letter:', err);
    } finally {
      setViewingLetterPathway(null);
    }
  }, [orgId, patientRecordId, viewingLetterPathway]);

  // A letter is visible to this viewer only once a clinician has sent it to
  // their role and the PDF has been rendered (TD-145). A combined referral can
  // surface both its ADHD and its autism letter.
  const availableTriageLetters = useMemo(
    () =>
      triageLetters.filter(
        (l) => l.hasPdf && !!viewerRoleSuffix && l.sharedRoles.includes(viewerRoleSuffix),
      ),
    [triageLetters, viewerRoleSuffix],
  );
  const triageLetterAvailable = availableTriageLetters.length > 0;

  // Scroll to top on mount - ensures user sees the page from the top when navigating
  // back from form pages (after submission). This provides a consistent UX where
  // the user doesn't land at the bottom of the forms list.
  useEffect(() => {
    try {
      window.scrollTo({ top: 0, behavior: 'instant' });
    } catch (_) {
      window.scrollTo(0, 0);
    }
  }, []);

  // Subscribe to the patient record for the referral context and derived flags.
  // Realtime (TD-156): the record changes while the user is on the page — the
  // HFP generates once both parties finish, triage forms are auto-assigned for
  // secondary referrals (TD-152), a letter is shared (TD-145), the intake
  // concern is derived — so a one-shot read left the page stale (e.g. the
  // Documents section stuck on its placeholder) until a manual refresh.
  useEffect(() => {
    if (!orgId || !patientRecordId) return undefined;
    const prRef = doc(db, 'rms_organisations', orgId, 'patientRecords', patientRecordId);
    const unsubscribe = onSnapshot(prRef, (snap) => {
      const data = snap.exists() ? (snap.data() || {}) : {};
      // Per-form progress mirror (TDI-154), resolved FIRST and outside the
      // parse guard below: the forms list must leave its loading state even if
      // some other field on the record fails to parse. A missing map
      // (pre-backfill record) folds to {} — every form reads as not started.
      setRecordFormProgress(
        data.formProgress && typeof data.formProgress === 'object' ? data.formProgress : {}
      );
      // Confirm-and-submit gate (TDI-415): the fields the zero-mock gating
      // module needs, plus the record mirror folded the backend's way
      // (camelCase, role-suffixed) — the page's own formProgress map uses the
      // viewer-relative kebab route keys and cannot feed the shared rule.
      setConfirmGate({
        requires: recordRequiresSubmissionConfirmation(data),
        patientLite: {
          harbour: data.harbour || null,
          hfpInstrument: data.hfpInstrument || null,
          intakeConcern: data.intakeConcern || null,
          referralPathway: data.referralPathway || null,
          schoolPhase: data?.schoolPhase || data?.school?.schoolType || null,
          triageFormsAssigned: data.triageFormsAssigned === true || data.triageFormsAssignedAt != null,
          submissionConfirmed: data.submissionConfirmed || null,
          // The SEND pathway's own confirmations (TDI-651), read when this
          // page confirms a SEN-only record.
          senSubmissionConfirmed: data.senSubmissionConfirmed || null,
          senPathway: data.senPathway || null,
          createdAt: data.createdAt || null,
        },
        ...buildFormProgressFromRecord(data),
      });
      try {
        setIsHarbourPupil(Boolean((data.harbour || {}).pupil));
        setHfpRecord({
          hfpInstrument: data.hfpInstrument || null,
          harbour: (data.harbour || {}).pupil ? { pupil: true } : null,
        });
        // Derive simple patient info for header
        const first = data.subject_first_name || data.firstName || data.subject?.firstName || '';
        const last = data.subject_last_name || data.lastName || data.subject?.lastName || '';
        const name = [first, last].filter(Boolean).join(' ').trim() || 'Patient';
        const dob = data.subject_dob || data.dob || data.subject?.dob || null;
        const createdAt = data.createdAt || null;
        setPatientInfo({ name, dob, createdAt, orgName: data.orgName || null });
        // Check if triage forms have already been assigned (set by rmsAssignTriageForms CF)
        const triageAssigned = data.triageFormsAssigned === true || data.triageFormsAssignedAt != null;
        setTriageFormsAssigned(triageAssigned);
        // HFP is only generated once both respondents complete; gate the Documents
        // section on its presence so we never show a not-yet-ready report
        // (v3 is the current generation; v2/v1 are legacy fallbacks).
        setHfpAvailable(isHfpReportAvailable(data));
        // School phase from the referral (secondary skips HFP + proceed-with-triage).
        setSchoolPhase(data?.schoolPhase || data?.school?.schoolType || null);
        // Intake concern (asd-only skips the ADHD SNAP-IV scale).
        setIntakeConcern(data?.intakeConcern || null);
        // Harbour SEN-only pupils show the HFP group alone.
        setSenOnlyRecord(isSenOnlyRecord(data));
        // Triage outcome letter sharing (TD-145). A combined referral holds a
        // letter per pathway (triagePathways.{pathway}.triageDocuments), so
        // build one entry per pathway; single-concern referrals fall back to
        // the top-level container.
        const letterPathways = pathwaysForConcern(data?.intakeConcern);
        const letters = (letterPathways.length ? letterPathways : [null]).map((pw) => {
          const td = (pw ? pathwayTriageDocuments(data, pw) : data?.triageDocuments) || {};
          return {
            pathway: pw,
            label: pw ? PATHWAY_LABELS[pw] : '',
            sharedRoles: Array.isArray(td.sharedRoles) ? td.sharedRoles : [],
            hasPdf: Boolean(td?.documentPaths?.pdf),
          };
        });
        setTriageLetters(letters);
        // Triage state for the referral status label (TD-14).
        setReferralTriage({
          status: data?.triage?.status || null,
          reviews: Array.isArray(data?.triageReviews) ? data.triageReviews : [],
        });
        // Modal dismissal maps (cross-device persistence) — streamed here so a
        // dismissal on another device takes effect without a reload.
        setModalDismissedRaw({
          completion: data?.completionModalDismissed || {},
          triage: data?.triageModalDismissed || {},
        });
      } catch (_) {
        // ignore
      }
    }, (_err) => {
      // The page keeps its last-known state on listener errors, but form
      // progress must never be stuck on loading (the old formInstances
      // listener resolved {} on error).
      setRecordFormProgress((prev) => (prev === null ? {} : prev));
    });
    return () => unsubscribe();
  }, [orgId, patientRecordId]);

  // Per-role modal-dismissed flags, derived from the streamed record. Keep the
  // "null = still loading" semantics the modal triggers rely on (they only fire
  // once the flag is explicitly false).
  const completionModalDismissedFromFirestore = useMemo(() => {
    if (modalDismissedRaw === null || !viewerRoleSuffix) return null;
    return modalDismissedRaw.completion?.[viewerRoleSuffix] === true;
  }, [modalDismissedRaw, viewerRoleSuffix]);
  const triageModalDismissedFromFirestore = useMemo(() => {
    if (modalDismissedRaw === null || !viewerRoleSuffix) return null;
    return modalDismissedRaw.triage?.[viewerRoleSuffix] === true;
  }, [modalDismissedRaw, viewerRoleSuffix]);

  // Resolve current viewer's role suffix for role-specific progress selection
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const u = auth?.currentUser;
        if (!u) return;
        const t = await u.getIdTokenResult(true);
        const userType = String((t?.claims?.userType || '')).toLowerCase();
        let role = null;
        if (userType.includes('school')) role = 'school_user';
        if (userType.includes('guardian')) role = 'guardian';
        if (!cancelled) setViewerRoleSuffix(role);
      } catch (_) {
        // no-op
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Real-time per-form progress, derived from the record's formProgress mirror
  // (streamed by the patient-record snapshot above). Shape is unchanged from
  // the old formInstances fold: instanceId -> { percent, status }.
  // Both fields are needed for accurate completion detection:
  // - percent: progressive value 0-100 during form completion
  // - status: 'submitted' definitively means 100% complete (passed validation)
  // Stays null until the viewer's role is known — this prevents showing
  // max-of-variants (often school user's) before the correct role is known.
  const rawInstanceProgress = useMemo(() => {
    if (viewerRoleSuffix === null || recordFormProgress === null) return null;
    const latest = {};
    Object.keys(recordFormProgress).forEach((key) => {
      if (!key) return;
      const entry = recordFormProgress[key] || {};
      latest[key] = {
        percent: Number(entry.percent ?? 0),
        status: entry.status || 'draft',
      };
    });
    return latest;
  }, [recordFormProgress, viewerRoleSuffix]);

  /**
   * The viewer's per-form progress map, derived from the raw snapshot.
   *
   * Key design decision: each user should ONLY see their own form progress.
   * If the viewer's role-specific form instance doesn't exist (not started),
   * return 0 — do NOT fall back to other roles' progress. A non-suffixed
   * instance is accepted for forms without role variants.
   *
   * INVARIANT: 100 means submitted; draft progress is capped at 99 so a
   * fully-drafted-but-unsubmitted form never reads as complete (see
   * completionPercent in referralStatus).
   */
  const formProgress = useMemo(() => {
    const latest = rawInstanceProgress || {};
    const pickProgress = (baseKey) => {
      const roleKey = viewerRoleSuffix ? `${baseKey}_${viewerRoleSuffix}` : null;
      if (roleKey && latest[roleKey] !== undefined) {
        return completionPercent(latest[roleKey]?.status, latest[roleKey]?.percent);
      }
      if (latest[baseKey] !== undefined) {
        return completionPercent(latest[baseKey]?.status, latest[baseKey]?.percent);
      }
      return 0; // instance doesn't exist yet — not started
    };
    return {
      // Referral forms
      'request-for-referral': pickProgress('requestForReferral'),
      'reason-for-referral': pickProgress('reasonForReferral'),
      'education-history': pickProgress('educationHistory'),
      'snap-iv-26': pickProgress('snapIV26'),
      // Holistic Functional Profiling forms (role-aware), both generations:
      // the record's instrument decides which of them are listed.
      ...Object.fromEntries(
        [...HFP_V2_FORM_KEYS, ...HFP_V3_FORM_KEYS].map((k) => [HARBOUR_FORM_SLUGS[k], pickProgress(k)]),
      ),
      // The guardian's referral history forms: part of the referral (Tom King, 28 October 2026).
      // These 7 forms collect comprehensive history for clinical triage
      'their-early-life': pickProgress('theirEarlyLife'),
      'their-family-and-home-life': pickProgress('theirFamilyAndHomeLife'),
      'their-lifestyle': pickProgress('theirLifestyle'),
      'their-history-with-the-law': pickProgress('theirHistoryWithTheLaw'),
      'their-family-history': pickProgress('theirFamilyHistory'),
      'their-medical-history': pickProgress('theirMedicalHistory'),
      'their-mental-health-history': pickProgress('theirMentalHealthHistory'),
    };
  }, [rawInstanceProgress, viewerRoleSuffix]);

  // The other party's completion status, derived LIVE from the record's
  // formProgress mirror (TD-156). The previous one-shot fetch only re-ran when the
  // viewer's own completion changed, so it went stale when the OTHER party
  // finished — the modal could say "waiting for the school representative"
  // while both parties were done (and the HFP already existed). The required
  // set is phase/concern-aware: secondary checks their referral forms, primary
  // their HFP forms (otherPartyRequiredFormKeys).
  const otherPartyCompletionStatus = useMemo(() => {
    const otherRole = viewerRoleSuffix
      ? (viewerRoleSuffix === 'school_user' ? 'guardian' : 'school_user')
      : null;
    if (!otherRole) return { complete: false, role: null };
    const requiredKeys = otherPartyRequiredFormKeys({ isAdhdConcern });
    return {
      complete: areRoleInstancesComplete(rawInstanceProgress, requiredKeys, otherRole),
      role: otherRole,
    };
  }, [rawInstanceProgress, viewerRoleSuffix, isAdhdConcern]);

  // Bundle resolver: which forms should this user see for this flow?
  const groups = useMemo(() => {
    // Base forms shared across flows; server-side schema gating adapts content
    const baseReferralItems = [
      { title: 'Request for Referral', subtitle: 'Contact and role', key: 'request-for-referral', formKey: 'requestForReferral' },
      { title: 'Reason for Referral', subtitle: 'Why you’re referring', key: 'reason-for-referral', formKey: 'reasonForReferral' },
      { title: 'Education History', subtitle: 'Setting, attendance and support', key: 'education-history', formKey: 'educationHistory' },
      // The ADHD Symptoms Scale (SNAP-IV) is ADHD-specific: it only appears once
      // an ADHD concern (adhd/both) has been logged by the school or the parent,
      // and stays hidden for autism-only and not-yet-logged referrals.
      ...(isAdhdConcern
        ? [{ title: 'ADHD Symptoms Scale', subtitle: 'ADHD behaviour rating scale', key: 'snap-iv-26', formKey: 'snapIV26' }]
        : []),
    ];

    // Build routes and include formKey for auto-complete functionality
    const withRoutes = (items) => items.map(it => ({
      title: it.title,
      subtitle: it.subtitle,
      key: it.key,
      formKey: it.formKey, // Backend schema key for auto-complete API
      progress: Number(formProgress[it.key] ?? 0),
      route: `/rms/patient/${encodeURIComponent(patientRecordId)}/forms/${it.key}`
    }));

    // Decide bundle (future: add role-specific or flow-specific differences)
    // For now, both school_user in school-initiated and guardian in guardian-initiated
    // see the same three forms; server schema handles copy/visibility/requiredness.
    const referralGroup = {
      title: 'Referral',
      type: 'referral-group',
      items: withRoutes(baseReferralItems),
    };

    // Holistic Functional Profiling (HFP) forms group
    // Displayed under "Strength and Support Needs" heading for continuity
    // The seven domains of the record's own instrument (hfpFormKeysForRecord).
    const strengthsScalesItems = hfpListItems(hfpFormKeys);
    const strengthsGroup = {
      title: 'Strength and Support Needs',
      type: 'strengths-group',
      items: withRoutes(strengthsScalesItems),
    };

    // Triage forms group (guardian-only, shown after assignment)
    // These 7 forms collect comprehensive history for clinical triage
    const triageFormsItems = [
      { title: 'Their Early Life', subtitle: 'Developmental history and milestones', key: 'their-early-life', formKey: 'theirEarlyLife' },
      { title: 'Their Family and Home Life', subtitle: 'Family structure and home environment', key: 'their-family-and-home-life', formKey: 'theirFamilyAndHomeLife' },
      { title: 'Their Lifestyle', subtitle: 'Daily routines, diet, sleep, and activities', key: 'their-lifestyle', formKey: 'theirLifestyle' },
      { title: 'Their History with the Law', subtitle: 'Any involvement with police or justice system', key: 'their-history-with-the-law', formKey: 'theirHistoryWithTheLaw' },
      { title: 'Their Family History', subtitle: 'Family medical and mental health background', key: 'their-family-history', formKey: 'theirFamilyHistory' },
      { title: 'Their Medical History', subtitle: 'Physical health, medications, and allergies', key: 'their-medical-history', formKey: 'theirMedicalHistory' },
      { title: 'Their Mental Health History', subtitle: 'Mental health diagnoses and support', key: 'their-mental-health-history', formKey: 'theirMentalHealthHistory' },
    ];
    const triageGroup = {
      title: 'Additional Information',
      type: 'triage-group',
      items: withRoutes(triageFormsItems),
    };

    // Build the final groups array. Referral Forms come first, then Strengths
    // and Support Scales (HFP) — hidden for secondary-school referrals (TD-129) —
    // then the guardian triage group once assigned. Visibility rules live in the
    // zero-mock gating module so they stay unit-tested.
    const { showStrengths, showTriage } = serviceUserFormGroupVisibility({
      isSecondaryReferral,
      viewerRoleSuffix,
      triageFormsAssigned,
    });

    // Harbour SEN-only pupil: the HFP forms are the whole ask. No referral
    // group (there is no referral) and no triage group (triage is a referral
    // concept). The moment a referral is added to the record, intakeConcern
    // lands and the full referral view returns.
    if (senOnlyRecord) {
      return [strengthsGroup];
    }

    const resultGroups = [referralGroup];
    if (showStrengths && strengthsGroup.items.length > 0) {
      resultGroups.push(strengthsGroup);
    }
    if (showTriage) {
      resultGroups.push(triageGroup);
    }

    return resultGroups;
  }, [patientRecordId, formProgress, viewerRoleSuffix, triageFormsAssigned, isSecondaryReferral, isAdhdConcern, senOnlyRecord, hfpFormKeys]);

  /**
   * All Forms Completion Detection
   * ------------------------------
   * Tracks when the current user has completed all their referral + HFP forms.
   * School users: 4 referral forms + 8 HFP forms = 12 total
   * Guardians: 4 referral forms + 8 HFP forms = 12 total (same forms, different answers)
   * 
   * When complete:
   * 1. Check other party's completion status
   * 2. Show celebration modal with appropriate next steps CTA
   */
  
  // Check if all required forms are complete (100% progress)
  // Uses REFERRAL_FORM_KEYS and the record's own HFP forms
  const allFormsComplete = useMemo(() => {
    // Which forms count toward completion (the referral's own; autism-only
    // drops the ADHD SNAP-IV scale; the strengths and needs forms are the
    // SEND pathway's, TDI-511) is decided by the zero-mock gating module so
    // the rule stays unit-tested.
    const allKeys = requiredReferralCompletionKeys({
      referralFormKeys: REFERRAL_FORM_KEYS,
      isAdhdConcern,
    });
    // Check that every form has 100% progress
    return allKeys.every(key => {
      const progress = Number(formProgress[key] ?? 0);
      return progress >= 100;
    });
  }, [formProgress, isAdhdConcern]);

  // Plain-language referral status shown to the service user (TD-14). Derived
  // from the triage state + this viewer's vs the other contributor's completion.
  // Harbour SEN-only pupils have no referral, so no referral status pill — a
  // label saying "Referral in progress" for a referral that does not exist
  // would be dishonest (clinical safety, not style).
  const referralStatus = senOnlyRecord
    ? null
    : getServiceUserReferralStatus({
        triageReviews: referralTriage.reviews,
        triageStatus: referralTriage.status,
        triageFormsAssigned,
        triageFormsComplete: TRIAGE_FORM_KEYS.every((key) => Number(formProgress[key] ?? 0) >= 100),
        allFormsComplete,
        otherPartyComplete: otherPartyCompletionStatus.complete,
      });

  // Check if all triage forms are complete (guardian-only)
  // These history forms are part of the referral, not a step after it
  const allTriageFormsComplete = useMemo(() => {
    // Only applicable if triage forms have been assigned
    if (!triageFormsAssigned) return false;
    
    return TRIAGE_FORM_KEYS.every(key => {
      const progress = Number(formProgress[key] ?? 0);
      return progress >= 100;
    });
  }, [formProgress, triageFormsAssigned]);
  
  /**
   * Trigger the completion modal when all forms are complete.
   * 
   * Persistence strategy (cross-device):
   * - Primary: Check Firestore `patientRecord.completionModalDismissed.{role}` for cross-device persistence
   * - Fallback: Use localStorage for same-session/device quick checks
   * - Key format: `rms:completionModalDismissed:${patientRecordId}:${role}`
   * - This prevents the modal from showing repeatedly after user closes it
   */
  const hasShownCompletionModalRef = useRef(false);
  
  // Check localStorage on mount as a quick initial check (Firestore check happens in separate effect)
  useEffect(() => {
    if (!patientRecordId || !viewerRoleSuffix) return;
    try {
      const dismissKey = `rms:completionModalDismissed:${patientRecordId}:${viewerRoleSuffix}`;
      const wasDismissed = localStorage.getItem(dismissKey);
      if (wasDismissed === 'true') {
        hasShownCompletionModalRef.current = true;
      }
    } catch (_) {
      // localStorage may be unavailable
    }
  }, [patientRecordId, viewerRoleSuffix]);
  
  // The HFP report is only generated once BOTH respondents have submitted all of
  // their HFP-dependent forms. The "your HFP is ready" celebration must therefore
  // fire ONCE, at that point — never on the viewer's own completion alone (which
  // previously produced a premature popup, and a second one when the HFP actually
  // became ready). Secondary referrals skip the HFP entirely, so for them the end
  // state is simply completing the referral forms.
  // Confirm-and-submit (TDI-415): offered once this viewer's own forms are all
  // in, on records gated on the step. Until they confirm, the confirm panel is
  // the one next action, so the celebration modal waits for it.
  const confirmPending = Boolean(
    confirmGate && confirmGate.requires && viewerRoleSuffix &&
    !isRoleSubmissionConfirmed(confirmGate.patientLite, viewerRoleSuffix) &&
    canConfirmSubmission({
      patient: confirmGate.patientLite,
      formProgress: confirmGate.formProgress,
      triageAssigned: confirmGate.triageAssigned,
      role: viewerRoleSuffix,
    })
  );
  const viewerConfirmed = Boolean(
    confirmGate && viewerRoleSuffix &&
    isRoleSubmissionConfirmed(confirmGate.patientLite, viewerRoleSuffix)
  );
  const handleConfirmSubmission = useCallback(async () => {
    setConfirmBusy(true);
    setConfirmFailed(false);
    try {
      await apiConfirmSubmission({ orgId, patientRecordId });
      // The record snapshot updates the panel away; nothing else to do here.
    } catch (_e) {
      setConfirmFailed(true);
    } finally {
      setConfirmBusy(false);
    }
  }, [orgId, patientRecordId]);

  const completionCelebrationReady = isSecondaryReferral
    ? allFormsComplete
    : (hfpAvailable || (allFormsComplete && otherPartyCompletionStatus.complete));

  useEffect(() => {
    // Only trigger the one-time celebration when:
    // 1. The HFP is genuinely ready (both parties done) — or the referral forms
    //    are complete for a secondary referral, which has no HFP
    // 2. We haven't shown the modal in this session (ref check)
    // 3. Firestore dismissal check has loaded and says not dismissed
    // 4. We know the viewer's role
    if (
      completionCelebrationReady &&
      !confirmPending && // the confirm-and-submit step is the one next action (TDI-415)
      !hasShownCompletionModalRef.current &&
      completionModalDismissedFromFirestore === false && // Explicitly false, not null (loading)
      viewerRoleSuffix
    ) {
      // Small delay to ensure the UI has updated
      const timer = setTimeout(() => {
        setIsAllFormsCompletedModalOpen(true);
        hasShownCompletionModalRef.current = true;
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [completionCelebrationReady, confirmPending, viewerRoleSuffix, completionModalDismissedFromFirestore]);

  // Handle modal close - persist dismissal to both Firestore (cross-device) and localStorage (quick check)
  const handleCompletionModalClose = useCallback(async () => {
    setIsAllFormsCompletedModalOpen(false);
    
    if (orgId && patientRecordId && viewerRoleSuffix) {
      // Save to localStorage immediately for quick same-device check
      try {
        const dismissKey = `rms:completionModalDismissed:${patientRecordId}:${viewerRoleSuffix}`;
        localStorage.setItem(dismissKey, 'true');
      } catch (_) {
        // localStorage may be unavailable
      }
      
      // Save to Firestore for cross-device/cross-session persistence
      try {
        const prRef = doc(db, 'rms_organisations', orgId, 'patientRecords', patientRecordId);
        await updateDoc(prRef, {
          [`completionModalDismissed.${viewerRoleSuffix}`]: true
        });
        // The record snapshot streams the write back, updating the derived flag.
        console.log(`[ServiceUserPatientOverview] Completion modal dismissal saved to Firestore for ${viewerRoleSuffix}`);
      } catch (err) {
        console.error('[ServiceUserPatientOverview] Error saving modal dismissal to Firestore:', err);
        // localStorage fallback already handled above
      }
    }
  }, [orgId, patientRecordId, viewerRoleSuffix]);

  /**
   * Triage Forms Completion Modal Trigger
   * 
   * Similar to the all-forms completion modal, but specifically for guardians who complete
   * all 7 triage forms. Shows a celebration and explains that the referral is now ready
   * for clinical review.
   */
  
  // Check localStorage for triage modal dismissal as quick initial check
  useEffect(() => {
    if (!patientRecordId || !viewerRoleSuffix || viewerRoleSuffix !== 'guardian') return;
    try {
      const dismissKey = `rms:triageModalDismissed:${patientRecordId}:${viewerRoleSuffix}`;
      const wasDismissed = localStorage.getItem(dismissKey);
      if (wasDismissed === 'true') {
        hasShownTriageModalRef.current = true;
      }
    } catch (_) {
      // localStorage may be unavailable
    }
  }, [patientRecordId, viewerRoleSuffix]);
  
  // Trigger triage completion modal when all triage forms are complete (guardian-only)
  useEffect(() => {
    // Only trigger for guardians who have:
    // 1. Completed all triage forms
    // 2. Haven't seen the modal in this session
    // 3. Haven't dismissed the modal previously (Firestore check)
    if (
      viewerRoleSuffix === 'guardian' &&
      allTriageFormsComplete &&
      !hasShownTriageModalRef.current &&
      triageModalDismissedFromFirestore === false // Explicitly false, not null (loading)
    ) {
      // Small delay to ensure UI has updated
      const timer = setTimeout(() => {
        setIsTriageCompleteModalOpen(true);
        hasShownTriageModalRef.current = true;
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [viewerRoleSuffix, allTriageFormsComplete, triageModalDismissedFromFirestore]);
  
  // Handle triage modal close - persist dismissal
  const handleTriageModalClose = useCallback(async () => {
    setIsTriageCompleteModalOpen(false);
    
    if (orgId && patientRecordId && viewerRoleSuffix) {
      // Save to localStorage immediately
      try {
        const dismissKey = `rms:triageModalDismissed:${patientRecordId}:${viewerRoleSuffix}`;
        localStorage.setItem(dismissKey, 'true');
      } catch (_) {
        // localStorage may be unavailable
      }
      
      // Save to Firestore for cross-device persistence
      try {
        const prRef = doc(db, 'rms_organisations', orgId, 'patientRecords', patientRecordId);
        await updateDoc(prRef, {
          [`triageModalDismissed.${viewerRoleSuffix}`]: true
        });
        // The record snapshot streams the write back, updating the derived flag.
      } catch (err) {
        console.error('[ServiceUserPatientOverview] Error saving triage modal dismissal:', err);
      }
    }
  }, [orgId, patientRecordId, viewerRoleSuffix]);

  const computed = useMemo(() => {
    const withGroupProgress = groups.map(g => {
      const total = g.items.length || 1;
      const sum = g.items.reduce((acc, it) => acc + (Number.isFinite(it.progress) ? it.progress : 0), 0);
      const avg = Math.round(sum / total);
      return { ...g, progress: avg };
    });
    const flat = withGroupProgress.flatMap(g => g.items);
    const overall = flat.length ? Math.round(flat.reduce((a, it) => a + (it.progress || 0), 0) / flat.length) : 0;
    return { groups: withGroupProgress, overall };
  }, [groups]);

  const scrollContainerRef = useRef(null);
  const nextRef = useRef(null);
  const timelineRef = useRef(null);
  const firstDotRef = useRef(null);
  const lastDotRef = useRef(null);
  const [lineDims, setLineDims] = useState({ top: 0, height: 0, left: null });
  // Flatten groups into a single sequence with group separators to render a vertical timeline.
  // Business rules (linear workflow):
  // - The first form with progress < 100 and a valid route is the "current" unlocked step.
  // - Forms after the current step are locked (unless explicitly configured otherwise).
  // - Forms without a route are treated as locked/coming-soon.
  const flatTimeline = useMemo(() => {
    const out = [];
    computed.groups.forEach((g, gi) => {
      // We include `groupType` so the renderer can apply small, group-specific UX tweaks
      // without relying on brittle string matches against titles.
      out.push({ type: 'group', groupType: g.type, title: g.title, progress: g.progress, groupIndex: gi });
      g.items.forEach((it, ii) => {
        out.push({ type: 'form', groupIndex: gi, itemIndex: ii, ...it });
      });
    });
    return out;
  }, [computed.groups]);

  const currentFormIndex = useMemo(() => {
    for (let i = 0; i < flatTimeline.length; i += 1) {
      const it = flatTimeline[i];
      if (it.type === 'form') {
        const pct = Number(it.progress || 0);
        if (it.route && pct < 100) return i;
      }
    }
    return -1; // All complete or no routable items
  }, [flatTimeline]);

  // Anchor indices for first/last form items (used to clip the rail between icons)
  const firstFormIndex = useMemo(() => {
    for (let i = 0; i < flatTimeline.length; i += 1) {
      if (flatTimeline[i].type === 'form') return i;
    }
    return -1;
  }, [flatTimeline]);
  const lastFormIndex = useMemo(() => {
    for (let i = flatTimeline.length - 1; i >= 0; i -= 1) {
      if (flatTimeline[i].type === 'form') return i;
    }
    return -1;
  }, [flatTimeline]);

  // Auto-scroll to current form on load is disabled to keep user at the top of the page.
  // Users can manually scroll to find their next form.
  // useEffect(() => {
  //   if (!scrollContainerRef.current || !nextRef.current) return;
  //   const timer = setTimeout(() => {
  //     try {
  //       nextRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  //     } catch (_) {}
  //   }, 300);
  //   return () => clearTimeout(timer);
  // }, [currentFormIndex]);

  // Compute measured rail (between first and last icons only)
  useEffect(() => {
    const compute = () => {
      try {
        const container = timelineRef.current;
        const firstEl = firstDotRef.current;
        const lastEl = lastDotRef.current;
        if (!container || !firstEl || !lastEl) return;
        const cRect = container.getBoundingClientRect();
        const fRect = firstEl.getBoundingClientRect();
        const lRect = lastEl.getBoundingClientRect();
        const fCenterY = fRect.top + fRect.height / 2;
        const lCenterY = lRect.top + lRect.height / 2;
        const fCenterX = fRect.left + fRect.width / 2;
        const top = Math.max(0, fCenterY - cRect.top);
        const height = Math.max(0, lCenterY - fCenterY);
        const left = fCenterX - cRect.left - 2; // center a ~4px wide rail
        setLineDims({ top, height, left });
      } catch (_) {}
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, [flatTimeline, currentFormIndex]);

  // Estimate "About X minutes remaining" text for each form.
  // For HFP forms, we calculate dynamically based on actual question counts and 15 seconds per question.
  // For other forms, we use fixed estimates based on typical completion times.
  
  // HFP form question counts derived from schema definitions
  // For forms with role-specific visibility (e.g., sleep questions in Energy & Motivation),
  // we provide separate counts for guardian vs school users.
  const HFP_QUESTION_COUNTS = {
    'hfp-thinking-and-learning': { all: 18 },
    'hfp-attention-and-memory': { all: 10 },
    'hfp-energy-and-motivation': { guardian: 9, school_user: 5 }, // 4 sleep questions are guardian-only
    'hfp-emotions-and-relationships': { all: 10 },
    'hfp-language-and-communication': { all: 13 },
    'hfp-movement-and-coordination': { all: 11 },
    'hfp-sensory-experiences': { all: 15 },
    'hfp-daily-life-participation': { all: 41 },
  };
  
  const SECONDS_PER_QUESTION = 15;
  
  // Get total question count for an HFP form, accounting for role-specific visibility
  const getHfpQuestionCount = (formKey) => {
    const counts = HFP_QUESTION_COUNTS[formKey];
    if (!counts) return null;
    // If all users see the same questions, use that count
    if (counts.all !== undefined) return counts.all;
    // Otherwise, use role-specific count based on current viewer
    return viewerRoleSuffix === 'guardian' ? counts.guardian : counts.school_user;
  };
  
  // Fixed time estimates for non-HFP forms (minutes)
  const getFormTotalMinutes = (formKey) => {
    switch (formKey) {
      case 'request-for-referral': return 8;
      case 'reason-for-referral': return 10;
      case 'education-history': return 12;
      case 'snap-iv-26': return 5;
      // Triage forms
      case 'their-early-life': return 15;
      case 'their-family-and-home-life': return 10;
      case 'their-lifestyle': return 8;
      case 'their-history-with-the-law': return 5;
      case 'their-family-history': return 10;
      case 'their-medical-history': return 12;
      case 'their-mental-health-history': return 10;
      default: return 10;
    }
  };
  
  const getTimeRemainingText = (formKey, progressPct) => {
    const pct = Math.max(0, Math.min(100, Number(progressPct || 0)));
    if (pct >= 100) return 'All questions answered';
    
    // For HFP forms, calculate dynamically based on question count
    const hfpQuestionCount = getHfpQuestionCount(formKey);
    if (hfpQuestionCount !== null) {
      const remainingQuestions = Math.ceil(hfpQuestionCount * (1 - pct / 100));
      const remainingSeconds = remainingQuestions * SECONDS_PER_QUESTION;
      const remainingMinutes = Math.ceil(remainingSeconds / 60);
      if (remainingMinutes < 1) return 'Less than 1 minute remaining';
      if (remainingMinutes === 1) return 'About 1 minute remaining';
      return `About ${remainingMinutes} minutes remaining`;
    }
    
    // For non-HFP forms, use fixed estimates
    const total = getFormTotalMinutes(formKey);
    const remaining = Math.round(total * (1 - pct / 100));
    if (remaining < 1) return 'Less than 1 minute remaining';
    if (remaining === 1) return 'About 1 minute remaining';
    return `About ${remaining} minutes remaining`;
  };

  if (isHarbourPupil) {
    const isGuardian = String(claims?.userType || '').toLowerCase().includes('guardian');
    const to = isGuardian
      ? `/rms/family/child/${encodeURIComponent(patientRecordId)}`
      : `/rms/pupils/${encodeURIComponent(patientRecordId)}`;
    return <Navigate to={to} replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-white to-secondary/15 flex flex-col">
      <div className="sticky top-0 left-0 right-0 bg-white z-30" style={{ boxShadow: '0 24px 36px rgba(255,255,255,0.95), 0 12px 20px rgba(255,255,255,0.85)' }}>
        <div className="w-full md:w-2/3 lg:w-3/4 xl:w-2/3 mx-auto px-6 md:px-10 pt-10 text-left">
          <div className="w-full mb-8 flex items-start justify-between gap-2 relative">
            {/* Expandable home button: shows icon only, expands on hover to reveal "Back to Dashboard" */}
            <button
              type="button"
              aria-label="Back to Dashboard"
              className="group inline-flex items-center gap-0 px-2 py-1.5 rounded-full bg-gray-100 hover:bg-primary/10 text-primary transition-all duration-300 ease-out hover:gap-2 hover:px-3 focus:outline-none focus:ring-2 focus:ring-primary/30"
              onClick={() => navigate('/rms/dashboard')}
            >
              <HomeIcon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
              <span className="max-w-0 overflow-hidden whitespace-nowrap opacity-0 group-hover:max-w-[150px] group-hover:opacity-100 transition-all duration-300 ease-out text-sm font-medium">
                Back to Dashboard
              </span>
            </button>          
            {/* People involved: expandable button that opens dialog */}
            <button
              type="button"
              aria-label="People involved in this referral"
              className="group inline-flex items-center gap-0 px-2 py-1.5 rounded-full bg-gray-100 hover:bg-primary/10 text-primary transition-all duration-300 ease-out hover:gap-2 hover:px-3 focus:outline-none focus:ring-2 focus:ring-primary/30"
              onClick={() => setIsPeopleDialogOpen(true)}
            >
              <UserGroupIcon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
              <span className="max-w-0 overflow-hidden whitespace-nowrap opacity-0 group-hover:max-w-[150px] group-hover:opacity-100 transition-all duration-300 ease-out text-sm font-medium">
                People Involved
              </span>
            </button>
          </div>
          {/* Header: patient details */}
          <h1 className="text-2xl font-bold text-gray-900 mb-1">{patientInfo?.name || 'Referral overview'}</h1>
          {/* Plain-language referral status for the service user (TD-14) */}
          {referralStatus?.label && (
            <span className="inline-flex items-center px-3 py-1 mb-2 rounded-full text-sm font-medium bg-primary/10 text-primary border border-primary/20">
              {referralStatus.label}
            </span>
          )}
          {/* <div className="text-sm text-gray-700 mb-1">
            Overall progress: {computed.overall}% {patientInfo?.dob ? `• DOB: ${patientInfo.dob}` : ''}
          </div> */}
          <div className="text-sm text-gray-600 mb-1">
            {patientInfo?.createdAt ? `Referred at ${formatCreatedAt(patientInfo.createdAt)}` : null}
          </div>
          {/* Flow context hint (dev/debug only) */}
          {/* {loadingCtx ? null : (
            <div className="text-xs text-gray-500 mb-2">
              TESTING ONLY: Referred by: {initiatedBy || 'unknown'} / Logged-in user's role: {userRole || 'unknown'}
            </div>
          )} */}
        </div>
      </div>

      <div className="h-12" />
      <div ref={scrollContainerRef} className="flex-grow w-full px-6 md:px-10 pb-28 text-left space-y-10">
        <div className="w-full md:w-2/3 lg:w-3/4 xl:w-2/3 mx-auto">
          {/* Documents — produced reports for this referral. The whole section only
              appears once BOTH respondents (school and guardian) have submitted their
              forms; until then there is nothing to show and it stays hidden. Once both
              are done, the HFP item appears as soon as the report exists
              (patientRecord.domains.hfp); in the brief window before it is generated an
              empty-state placeholder is shown. Both roles get access at the same time.
              Rendered from a list so further documents (e.g. triage outcome letters) can
              be added later. "Download" is disabled until the PDF render lands (B4). */}
          {(hfpAvailable || triageLetterAvailable || (allFormsComplete && otherPartyCompletionStatus.complete)) && (
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Documents</h2>
            {(hfpAvailable || triageLetterAvailable) ? (
            <ul className="space-y-3">
              {hfpAvailable && (
                <li className="flex flex-col sm:flex-row sm:items-start gap-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <div className="flex items-start gap-4 flex-grow min-w-0">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <DocumentTextIcon className="w-5 h-5 text-primary" aria-hidden="true" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-base font-semibold text-gray-900">Holistic Functional Profile</div>
                      {/* Role-neutral copy: this overview is shown to both school
                          representatives and legal guardians, so we avoid parent-centric
                          phrasing ("your child") and use the child's name where available. */}
                      <p className="text-sm text-gray-600">
                        An overview of {(patientInfo?.name || '').trim().split(/\s+/)[0] || 'the child'}'s strengths and support needs at home and at school.
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-2 sm:pl-0 pl-14">
                    <button
                      type="button"
                      onClick={handleViewHfpV2}
                      className="px-3 py-2 rounded-lg border border-primary bg-primary text-white text-sm font-semibold shadow-sm hover:bg-primary/90 transition-colors"
                    >
                      View
                    </button>
                    {/* Download becomes available once the server-side PDF render lands (B4). */}
                    <button
                      type="button"
                      disabled
                      title="Download will be available soon"
                      aria-label="Download (available soon)"
                      className="px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-gray-400 text-sm font-semibold cursor-not-allowed"
                    >
                      Download
                    </button>
                  </div>
                </li>
              )}
              {availableTriageLetters.map((letter) => {
                const firstName = (patientInfo?.name || '').trim().split(/\s+/)[0];
                const title = letter.label
                  ? `${letter.label} Triage Outcome Letter`
                  : 'Triage Outcome Letter';
                const opening = viewingLetterPathway === (letter.pathway || 'default');
                return (
                  <li
                    key={letter.pathway || 'default'}
                    className="flex flex-col sm:flex-row sm:items-start gap-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
                  >
                    <div className="flex items-start gap-4 flex-grow min-w-0">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <DocumentTextIcon className="w-5 h-5 text-primary" aria-hidden="true" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-base font-semibold text-gray-900">{title}</div>
                        <p className="text-sm text-gray-600">
                          The outcome of the referral{firstName ? ` for ${firstName}` : ''}.
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-shrink-0 items-center gap-2 sm:pl-0 pl-14">
                      <button
                        type="button"
                        onClick={() => handleViewTriageLetter(letter.pathway)}
                        disabled={!!viewingLetterPathway}
                        className="px-3 py-2 rounded-lg border border-primary bg-primary text-white text-sm font-semibold shadow-sm hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {opening ? 'Opening…' : 'View'}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
            ) : (
              <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-5 text-sm text-gray-600">
                {isSecondaryReferral
                  ? 'No documents are available yet. Any triage outcome letters will appear here once they are ready.'
                  : 'No documents are available yet. The Holistic Functional Profile and any triage outcome letters will appear here once they are ready.'}
              </div>
            )}
          </section>
          )}

        </div>
        <div className="w-full md:w-1/2 lg:w-2/5 mx-auto">          
          {/* Connected vertical timeline with linear workflow (group separators) */}
          <div ref={timelineRef} className="relative w-full">
            {lineDims.height > 0 && lineDims.left !== null && (
              <div
                className="absolute bg-primary rounded-full"
                style={{ top: `${lineDims.top}px`, height: `${lineDims.height}px`, left: `${lineDims.left}px`, width: '4px' }}
                aria-hidden="true"
              />
            )}
            <ol className="space-y-2">
              {flatTimeline.map((node, idx) => {
                if (node.type === 'group') {
                  // Group separator: extra spacing and show group title + mini progress bar
                  return (
                    <li key={`group-${node.title}-${idx}`} className="mt-6 mb-1 pt-4 pl-12">
                      <div className="flex items-center gap-2 text-xl font-bold text-gray-900">
                        <span>{node.title}</span>
                        {/**
                         * Guardian-only tooltip for the "Additional Information" section.
                         *
                         * Business context (plain English):
                         * - This section only appears for guardians once triage forms are assigned.
                         * - We add a short explanation so guardians understand *why* these extra forms exist,
                         *   which reduces confusion and improves completion rates.
                         *
                         * Implementation note:
                         * - We gate on `viewerRoleSuffix` (role) and `groupType` (structure) instead of only the title,
                         *   to make future copy changes safer.
                         */}
                        {viewerRoleSuffix === 'guardian' &&
                          node.groupType === 'triage-group' &&
                          node.title === 'Additional Information' && (
                            <InfoTooltip
                              content="Complete the forms below to help clinicians prepare for the assessment."
                              label="More information about Additional Information"
                            />
                          )}
                      </div>
                    </li>
                  );
                }

                const progress = Number(node.progress || 0);
                const isComplete = progress >= 100;
                const isCurrent = !isComplete && currentFormIndex === idx && !!node.route;
                const isLocked = !isComplete && !isCurrent && (!node.route || (currentFormIndex >= 0 && idx > currentFormIndex));
                const isClickable = !!node.route && (isCurrent || isComplete);
                const timeText = getTimeRemainingText(node.key, node.progress);
                const refIsCurrent = isCurrent ? nextRef : undefined;

                // Make each form card more compact by lowering padding and slightly reducing title size
                const compactBtn = "w-full text-left px-3 py-3 rounded-xl border transition duration-150 focus:outline-none focus:ring-2 shadow-sm";
                const stateStyles = isComplete
                  ? "bg-green-50 border-2 border-green-500"
                  : isCurrent
                    ? "bg-amber-50 border-2 border-amber-400"
                    : isLocked
                      ? "bg-gray-100 border-2 border-gray-300 opacity-80 cursor-not-allowed"
                      : "bg-white border-2 border-gray-300";
                const hoverStyles = isLocked
                  ? ""
                  : (isComplete
                      ? "hover:bg-green-100 hover:border-green-500"
                      : (isCurrent
                          ? "hover:bg-amber-100 hover:border-primary-400"
                          : "hover:bg-gray-50 hover:border-gray-300"));
                const labelStyles = isLocked ? "text-gray-400" : "text-gray-900";
                const subLabelStyles = isLocked ? "text-gray-400" : "text-gray-600";

                return (
                    <li key={`form-${node.title}-${idx}`} className="relative pl-12">
                    {/* Left status dot */}
                     <div
                       className="absolute left-[20px] -translate-x-1/2 top-1/2 -translate-y-1/2 z-10"
                       ref={idx === firstFormIndex ? firstDotRef : (idx === lastFormIndex ? lastDotRef : undefined)}
                       aria-hidden="true"
                      >
                        {isComplete ? (
                          <span className="relative inline-flex items-center justify-center w-8 h-8 rounded-full">
                            <span className="absolute inset-0 m-auto w-5 h-5 rounded-full bg-white" />
                            <CheckCircleIcon className="relative w-8 h-8 text-green-500" />
                          </span>
                      ) : isLocked ? (
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full border-2 border-gray-400 text-gray-400 bg-white ">
                          <LockClosedIcon className="w-4 h-4" />
                        </span>
                      ) : progress > 0 ? (
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full border-2 border-amber-400 text-amber-500 bg-white">
                          <svg viewBox="0 0 20 20" className="w-3.5 h-3.5">
                            <circle cx="10" cy="10" r="8" fill="currentColor" />
                          </svg>
                        </span>
                      ) : (
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full border-2 border-gray-400 text-gray-400 bg-white">
                          <svg viewBox="0 0 20 20" className="w-3.5 h-3.5">
                            <circle cx="10" cy="10" r="8" fill="currentColor" className="opacity-30" />
                          </svg>
                        </span>
                      )}
                    </div>

                    {/* Step content - wrapped in a flex container to allow sibling auto-complete button */}
                    <div className="flex items-center gap-2 w-full">
                      <button
                        ref={refIsCurrent}
                        type="button"
                        className={`${compactBtn} ${stateStyles} ${hoverStyles} pl-4 pr-4 flex-1`}
                        style={{ '--tw-ring-color': '#e7c22f', '--tw-ring-opacity': '0.5' }}
                        aria-disabled={isLocked || !isClickable}
                        onClick={() => {
                          if (!isClickable) return;
                          const isDone = isComplete;
                          navigate(isDone ? `${node.route}?mode=review` : node.route);
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className={`text-base font-semibold ${labelStyles}`}>{node.title}</div>
                            {/* Always show a subtitle; for completed forms, state completion explicitly */}
                            <div className={`text-xs ${subLabelStyles}`}>
                              {isComplete
                                ? 'All questions completed'
                                : timeText}
                            </div>
                          </div>
                          {!isLocked && 
                          <div className="flex items-center gap-2">
                            <svg viewBox="0 0 20 20" className="w-4 h-4 text-gray-500">
                              <path d="M7 5l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </div>
                          }
                        </div>
                      </button>
                      {/* Dev-only auto-complete button for demos - placed outside the main button to avoid nesting */}
                      {isDev && node.formKey && !isLocked && (
                        <AutoCompleteButton
                          formKey={node.formKey}
                          orgId={orgId}
                          patientRecordId={patientRecordId}
                          selfComplete={true}
                          isComplete={isComplete}
                          onComplete={(fk, role, result) => {
                            console.log(`[ServiceUserPatientOverview] Form auto-completed: ${fk}`, result);
                            // Form progress will auto-refresh via the Firestore snapshot listener
                          }}
                        />
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>

            {/* ─── Guardian-only: Assign Triage Forms Button ─────────────────────
                This button is only visible to guardian users who haven't yet been
                assigned the 7 history forms. Clicking opens a dialog
                explaining what forms will be added and allows them to confirm.
            */}
            {/* Confirm and submit (TDI-415): the one final step once this
                viewer's forms are all in. The form list above is the record
                of what they completed, so the panel does not repeat it. */}
            {confirmPending && (
              <div className="mt-8 pt-6 border-t border-gray-200" data-testid="confirm-submission-panel">
                <ConfirmAndSubmitScreen
                  statementDetail={senOnlyRecord
                    ? 'The profile is shared once you and the school have both confirmed.'
                    : `The referral is sent to the clinical team once you and the ${viewerRoleSuffix === 'school_user' ? 'parent or carer' : 'school'} have both confirmed.`}
                  busy={confirmBusy}
                  error={confirmFailed}
                  onConfirm={handleConfirmSubmission}
                />
              </div>
            )}
            {viewerConfirmed && (
              <div className="mt-8 pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-700" role="status">
                  Thank you. You confirmed and submitted your answers, so they can no longer be changed.
                </p>
              </div>
            )}
            {shouldShowAssignTriageFormsPanel({ viewerRoleSuffix, triageFormsAssigned, allFormsComplete, schoolPhase }) && (
              <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20 rounded-xl p-5">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <ClipboardListIcon className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-grow">
                      <h3 className="text-base font-semibold text-gray-900 mb-1">
                       Want to request an assessment?
                      </h3>
                      <p className="text-sm text-gray-600 mb-4">
                        Help clinicians prepare for the assessment by sharing additional 
                        details about your child's history, health, and development.
                      </p>
                      <button
                        type="button"
                        onClick={() => setIsTriageFormsDialogOpen(true)}
                        className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium shadow-sm"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Request an assessment
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}            
          </div>
        </div>
      </div>
      {/* Mounted once at the page level so it can be opened from header; controlled for accessibility */}
      <PeopleDialog
        isOpen={isPeopleDialogOpen}
        onOpenChange={setIsPeopleDialogOpen}
        patientRecordId={patientRecordId}
        viewerRole={viewerRoleSuffix}
      />

      {/* Assign Triage Forms Dialog - guardians self-assign their referral history forms */}
      <AssignTriageFormsDialog
        isOpen={isTriageFormsDialogOpen}
        onOpenChange={setIsTriageFormsDialogOpen}
        patientRecordId={patientRecordId}
        onSuccess={() => {
          // Mark triage forms as assigned locally so the button disappears
          // and a confirmation message is shown instead
          setTriageFormsAssigned(true);
        }}
      />

      {/* The HFP v2 profile opens on its own page (/rms/patient/:id/hfp),
          mirroring the clinician view. */}

      {/* All Forms Completed Modal - celebration and next steps (info-only) */}
      <RmsAllFormsCompletedModal
        isOpen={isAllFormsCompletedModalOpen}
        onClose={handleCompletionModalClose}
        // The profile is the SEND pathway's, not the referral's (TDI-511):
        // completing the referral forms never offers one. It is offered only
        // when the record carries the scored report.
        onViewHfp={hfpAvailable ? handleViewHfpV2 : undefined}
        otherPartyComplete={otherPartyCompletionStatus.complete}
        otherPartyRole={otherPartyCompletionStatus.role}
        isSecondaryReferral={isSecondaryReferral}
        viewerRole={viewerRoleSuffix}
        patientRecordId={patientRecordId}
        orgId={orgId}
        patientName={patientInfo?.name}
      />

      {/* Triage Forms Completed Modal - shown when guardian completes all additional forms */}
      <RmsTriageCompleteModal
        isOpen={isTriageCompleteModalOpen}
        onClose={handleTriageModalClose}
        onViewHfp={hfpAvailable ? handleViewHfpV2 : null}
        orgName={patientInfo?.orgName || 'the clinic'}
        patientName={patientInfo?.name}
        hasCompletedHfp={hfpAvailable}
        isSecondaryReferral={isSecondaryReferral}
      />
                        </div>
  );
}

